import { Component, OnInit } from '@angular/core';
import { UserService } from '@admin/core/user.service';
import { FeatureEnum } from '@admin/shared/models/feature-enum';
import { PagingFilter } from '@admin/shared/models/paging-filter.model';
import { ConfigurationService } from '@admin/shared/services/configuration.service';
import { LookupService } from '@admin/shared/services/lookup.service';
import { LazyLoadEvent, SelectItem } from 'primeng/api';
import { CustomerBrandingAccessTokenFilterModel } from '../shared/models/customer-branding-access-token-filter-model';
import { CustomerBrandingAccessTokenModel } from '../shared/models/customer-branding-access-token-model';
import { CustomerBrandingAccessTokenStatus } from '../shared/models/customer-branding-access-token-status.enum';
import { ProductType } from '../shared/models/product-type';
import { StoreItemModel } from '../shared/models/store-item-model';
import { BrandingProductServiceService } from '../shared/services/branding-product-service.service';
import { StoreService } from '../shared/services/store-service.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-branding-product-request-list',
  templateUrl: './branding-product-request-list.component.html',
  styleUrls: ['./branding-product-request-list.component.less'],
  standalone: false,
})
export class BrandingProductRequestListComponent implements OnInit {
  public filterPagingWrapper: PagingFilter<CustomerBrandingAccessTokenFilterModel>;
  public storeItems: StoreItemModel[];
  public brandingRequestList: CustomerBrandingAccessTokenModel[];
  public totalRecords: number;
  public dateFormat: string;
  public specialDateFormat: string;
  public pageSize: number;

  public hasRequestApproveRejectPermission: boolean;
  public hasRequestTerminationPermission: boolean;

  public switchFilterStates: SelectItem[];

  public brandingAccessTokenStatusEnum = CustomerBrandingAccessTokenStatus;

  constructor(
    private lookupService: LookupService,
    private config: ConfigurationService,
    private brandingProductServiceService: BrandingProductServiceService,
    private userService: UserService,
    private storeService: StoreService,
    private translateService: TranslateService
  ) {
    this.hasRequestApproveRejectPermission = false;
    this.hasRequestTerminationPermission = false;

    this.filterPagingWrapper = new PagingFilter(
      new CustomerBrandingAccessTokenFilterModel()
    );
    this.filterPagingWrapper.filter.statusIds = [
      CustomerBrandingAccessTokenStatus.Pending,
    ];
    this.brandingRequestList = [];
    this.switchFilterStates = [
      {
        label: this.translateService.instant('label.Pending'),
        value: [CustomerBrandingAccessTokenStatus.Pending],
      },
      {
        label: this.translateService.instant('label.Active'),
        value: [CustomerBrandingAccessTokenStatus.Active],
      },
      {
        label: this.translateService.instant('label.Expired'),
        value: [CustomerBrandingAccessTokenStatus.Expired],
      },
      {
        label: this.translateService.instant('label.Rejected'),
        value: [CustomerBrandingAccessTokenStatus.Rejected],
      },
      {
        label: this.translateService.instant('label.Terminated'),
        value: [CustomerBrandingAccessTokenStatus.Terminated],
      },
    ];
  }

  onLoad(event: LazyLoadEvent) {
    this.filterPagingWrapper.pagingInfo.start = event.first;
    this.filterPagingWrapper.pagingInfo.limit = event.rows;
    let filters: { [index: string]: any } = [];

    if (event.filters != null) {
      for (let f in event.filters) {
        {
          filters[f] = event.filters[f].value;
        }
      }
    }

    if (event.sortField && event.sortOrder) {
      this.filterPagingWrapper.sortParameters = [
        {
          column: event.sortField,
          order: event.sortOrder == 1 ? 'asc' : 'desc',
        },
      ];
    }

    this.filterPagingWrapper.filter.customer = filters['fullName'];
    this.onFilter();
  }

  onFilter() {
    this.brandingProductServiceService
      .listBrandingRequests(this.filterPagingWrapper)
      .subscribe((d) => {
        this.brandingRequestList = d.items;
        this.totalRecords = d.totalCount;
      });
  }

  onApproveRequestClick(item: CustomerBrandingAccessTokenModel) {
    if (confirm(this.translateService.instant('message.AreYouSure'))) {
      if (item.storeItemId !== item.storeItem.itemId) {
        this.brandingProductServiceService
          .approveBrandingRequestWithModification(item, item.storeItemId)
          .subscribe((resp) => {
            this.onFilter();
          });
      } else {
        this.brandingProductServiceService
          .approveBrandingRequest(item)
          .subscribe((resp) => {
            this.onFilter();
          });
      }
    }
  }

  onRejectRequestClick(item: CustomerBrandingAccessTokenModel) {
    if (confirm(this.translateService.instant('message.AreYouSure'))) {
      this.brandingProductServiceService
        .rejectBrandingRequest(item)
        .subscribe((resp) => {
          this.onFilter();
        });
    }
  }

  onRevokeRequestClick(result: any) {
    if (result.agree) {
      this.brandingProductServiceService
        .revokeBrandingRequest(result.id, result.checked)
        .subscribe((resp) => {
          this.onFilter();
        });
    }
  }

  getSpecialDateFormat(format: string) {
    return format.replace('MM', 'mm').replace('yyyy', 'yy');
  }

  ngOnInit() {
    const config = this.config.getCachedConfig();
    this.pageSize = config.defaultPageSize;
    this.dateFormat = config.dateFormat;
    this.specialDateFormat = this.getSpecialDateFormat(this.dateFormat);

    const userFeatures = this.userService.currentUser.features;
    this.hasRequestApproveRejectPermission = userFeatures.some(
      (f) => f === FeatureEnum.ApproveRejectCustomerBrandingProductRequest
    );
    this.hasRequestTerminationPermission = userFeatures.some(
      (f) => f === FeatureEnum.TerminateActiveCustomerBrandingProduct
    );

    this.storeService
      .listStockItems(ProductType.CustomerBranding)
      .subscribe((resp) => {
        this.storeItems = resp.items;
      });
  }
}
